import { useState } from "react";
import { Check, Copy, Download, FileArchive, KeyRound, LifeBuoy, Loader2, MessageSquare, RefreshCw, ShieldCheck, Trash2, Upload, UserPlus, Users } from "lucide-react";
import { api, API } from "@/lib/api";

export default function Admin() {
  const [token, setToken] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [build, setBuild] = useState(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [adding, setAdding] = useState(false);
  const [addMsg, setAddMsg] = useState(null);
  const [copied, setCopied] = useState("");
  const [messages, setMessages] = useState([]);
  const [threads, setThreads] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [expandedReplies, setExpandedReplies] = useState([]);

  const normalizedToken = (() => {
    const v = token.trim();
    if (!v) return v;
    return v.startsWith("cnc-admin-") ? v : `cnc-admin-${v}`;
  })();
  const authHeaders = { "X-Admin-Token": normalizedToken };

  const refreshBuild = async () => {
    const b = await api.get("/admin/build", { headers: authHeaders });
    setBuild(b.data);
  };

  const load = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/admin/subscribers", { headers: authHeaders });
      setData(res.data);
      await refreshBuild();
      const sm = await api.get("/admin/support", { headers: authHeaders });
      setMessages(sm.data.messages);
      const ft = await api.get("/admin/forum/threads", { headers: authHeaders });
      setThreads(ft.data.threads);
    } catch (err) {
      setData(null);
      setBuild(null);
      setError(err.response?.status === 401 ? "Invalid admin token — paste the full token (cnc-admin-…)." : "Could not load registrations.");
    } finally {
      setLoading(false);
    }
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return;
    setUploading(true);
    setUploadMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/admin/build`, { method: "POST", headers: authHeaders, body: fd });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof json.detail === "string" ? json.detail : `Upload failed (${res.status})`);
      setUploadMsg(`Build updated: ${json.filename} (${(json.size / 1024).toFixed(1)} KB)`);
      setFile(null);
      await refreshBuild();
    } catch (err) {
      setUploadMsg(err.message);
    } finally {
      setUploading(false);
    }
  };

  const addEmail = async (e) => {
    e.preventDefault();
    setAdding(true);
    setAddMsg(null);
    try {
      const res = await api.post("/admin/subscribers", { email: newEmail }, { headers: authHeaders });
      setAddMsg({ email: res.data.email, link: `${API}/download?token=${res.data.token}`, status: res.data.status });
      setNewEmail("");
      const list = await api.get("/admin/subscribers", { headers: authHeaders });
      setData(list.data);
    } catch (err) {
      setAddMsg({ error: err.response?.data?.detail || "Could not add email." });
    } finally {
      setAdding(false);
    }
  };

  const copyLink = async (tok, email) => {
    await navigator.clipboard.writeText(`${API}/download?token=${tok}`);
    setCopied(email);
    setTimeout(() => setCopied(""), 2000);
  };

  const refreshList = async () => {
    const list = await api.get("/admin/subscribers", { headers: authHeaders });
    setData(list.data);
  };

  const removeEmail = async (email) => {
    if (!window.confirm(`Remove ${email} from the list?`)) return;
    try {
      await api.delete(`/admin/subscribers/${encodeURIComponent(email)}`, { headers: authHeaders });
      await refreshList();
    } catch {
      setError("Could not remove that email.");
    }
  };

  const clearAll = async () => {
    if (!window.confirm("Delete ALL registrations? This cannot be undone.")) return;
    try {
      await api.post("/admin/clear", {}, { headers: authHeaders });
      await refreshList();
    } catch {
      setError("Could not clear the list.");
    }
  };

  const deleteMessage = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await api.delete(`/admin/support/${id}`, { headers: authHeaders });
      const sm = await api.get("/admin/support", { headers: authHeaders });
      setMessages(sm.data.messages);
    } catch {
      setError("Could not delete the message.");
    }
  };

  const deleteThread = async (id) => {
    if (!window.confirm("Delete this thread and all its replies?")) return;
    try {
      await api.delete(`/admin/forum/threads/${id}`, { headers: authHeaders });
      const ft = await api.get("/admin/forum/threads", { headers: authHeaders });
      setThreads(ft.data.threads);
      if (expanded === id) setExpanded(null);
    } catch {
      setError("Could not delete the thread.");
    }
  };

  const toggleThread = async (id) => {
    if (expanded === id) {
      setExpanded(null);
      return;
    }
    try {
      const { data } = await api.get(`/admin/forum/threads/${id}`, { headers: authHeaders });
      setExpanded(id);
      setExpandedReplies(data.replies);
    } catch {
      setError("Could not load the thread.");
    }
  };

  const deleteReply = async (rid) => {
    if (!window.confirm("Delete this reply?")) return;
    try {
      await api.delete(`/admin/forum/replies/${rid}`, { headers: authHeaders });
      setExpandedReplies(expandedReplies.filter((r) => r.id !== rid));
      const ft = await api.get("/admin/forum/threads", { headers: authHeaders });
      setThreads(ft.data.threads);
    } catch {
      setError("Could not delete the reply.");
    }
  };

  const exportCsv = async () => {
    setError("");
    try {
      const res = await fetch(`${API}/admin/export`, { headers: { "X-Admin-Token": token } });
      if (res.status === 401) throw new Error("Invalid admin token — paste the full token (cnc-admin-…).");
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "cncroutersoft-verified-emails.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <main className="min-h-screen bg-[#050505] text-[#F5F5F5] px-6 sm:px-12 py-16" data-testid="admin-page">
      <div className="max-w-4xl mx-auto">
        <p className="label-mono mb-2 flex items-center gap-3">
          <span className="inline-block w-8 h-px bg-[#FF3B00]" /> Restricted
        </p>
        <h1 className="font-display font-bold uppercase tracking-tight text-4xl sm:text-5xl">
          Registration list
        </h1>

        <form onSubmit={load} className="mt-10 flex flex-col sm:flex-row gap-3" data-testid="admin-token-form">
          <div className="flex-1 flex items-center gap-3 border-b border-white/25 focus-within:border-[#FF3B00] transition-colors">
            <KeyRound className="w-4 h-4 text-white/40" />
            <input
              data-testid="admin-token-input"
              type="password"
              autoComplete="off"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Admin token"
              className="flex-1 bg-transparent outline-none py-3 font-mono2 text-sm placeholder:text-white/25"
            />
          </div>
          <button data-testid="admin-load-button" type="submit" disabled={!token || loading} className="btn-forge">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-4 h-4" /> Load list</>}
          </button>
        </form>
        {error && <p className="mt-3 font-mono2 text-xs text-[#FF3B00]" data-testid="admin-error">{error}</p>}

        {data && (
          <div className="mt-12" data-testid="admin-dashboard">
            <div className="grid-cell p-6 sm:p-8 mb-10" data-testid="build-panel">
              <div className="flex items-center gap-3 mb-5">
                <FileArchive className="w-5 h-5 text-[#FF3B00]" />
                <h2 className="font-display uppercase tracking-tight text-2xl">Download build</h2>
              </div>
              {build && (
                <div className="font-mono2 text-xs leading-loose text-white/60 mb-6" data-testid="build-info">
                  <p>CURRENT ... <span className="text-white">{build.filename}</span></p>
                  <p>SIZE ...... {(build.size / 1024).toFixed(1)} KB</p>
                  <p>UPDATED ... {build.updated_at?.slice(0, 19).replace("T", " ")} UTC</p>
                </div>
              )}
              <form onSubmit={upload} className="flex flex-col sm:flex-row gap-3 sm:items-end" data-testid="build-upload-form">
                <label className="flex-1 cursor-pointer border border-dashed border-white/25 hover:border-[#FF3B00] transition-colors px-4 py-3 font-mono2 text-xs text-white/50" data-testid="build-file-label">
                  {file ? <span className="text-white">{file.name}</span> : "Choose replacement .zip…"}
                  <input
                    data-testid="build-file-input"
                    type="file"
                    accept=".zip"
                    className="hidden"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                  />
                </label>
                <button data-testid="build-upload-button" type="submit" disabled={!file || uploading} className="btn-forge">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4" /> Upload & replace</>}
                </button>
              </form>
              {uploadMsg && <p className="mt-3 font-mono2 text-xs text-[#00FF41]" data-testid="build-upload-message">{uploadMsg}</p>}
              <p className="mt-4 font-mono2 text-[11px] text-white/35 uppercase tracking-[0.2em]">
                Uploading replaces the file every verified visitor downloads.
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8">
              <div className="grid-cell p-6">
                <p className="label-mono flex items-center gap-2"><Users className="w-3.5 h-3.5" /> Total</p>
                <p className="font-display font-bold text-4xl mt-2" data-testid="admin-total">{data.total}</p>
              </div>
              <div className="grid-cell p-6">
                <p className="label-mono flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5" /> Verified</p>
                <p className="font-display font-bold text-4xl mt-2 text-[#00FF41]" data-testid="admin-verified">{data.verified}</p>
              </div>
              <div className="grid-cell p-6 col-span-2 sm:col-span-1 flex items-center">
                <button data-testid="admin-export-button" onClick={exportCsv} className="btn-forge w-full">
                  <Download className="w-4 h-4" /> Export verified CSV
                </button>
              </div>
            </div>

            <div className="grid-cell p-6 sm:p-8 mb-8" data-testid="add-email-panel">
              <div className="flex items-center gap-3 mb-3">
                <UserPlus className="w-5 h-5 text-[#FF3B00]" />
                <h2 className="font-display uppercase tracking-tight text-2xl">Add a downloader</h2>
              </div>
              <p className="text-white/60 text-sm mb-5 leading-relaxed">
                Add an email by hand — it's marked verified immediately and you get a personal download link to send them. No confirmation email needed.
              </p>
              <form onSubmit={addEmail} className="flex flex-col sm:flex-row gap-3" data-testid="add-email-form">
                <input
                  data-testid="add-email-input"
                  type="email"
                  required
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="tester@workshop.com"
                  className="flex-1 bg-transparent border-b border-white/25 focus:border-[#FF3B00] outline-none py-3 font-mono2 text-sm placeholder:text-white/25 transition-colors"
                />
                <button data-testid="add-email-button" type="submit" disabled={!newEmail || adding} className="btn-forge">
                  {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UserPlus className="w-4 h-4" /> Add & get link</>}
                </button>
              </form>
              {addMsg && (
                addMsg.error ? (
                  <p className="mt-3 font-mono2 text-xs text-[#FF3B00]" data-testid="add-email-error">{addMsg.error}</p>
                ) : (
                  <div className="mt-4 border border-dashed border-[#00FF41]/40 bg-[#00FF41]/5 p-4" data-testid="add-email-result">
                    <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#00FF41] mb-2">
                      {addMsg.status === "exists" ? "Already on the list — here is their link" : `Added ${addMsg.email} — send them this link`}
                    </p>
                    <div className="flex items-center gap-3">
                      <code className="font-mono2 text-xs text-white/80 break-all flex-1" data-testid="add-email-link">{addMsg.link}</code>
                      <button
                        data-testid="add-email-copy"
                        onClick={() => { navigator.clipboard.writeText(addMsg.link); setCopied("new"); setTimeout(() => setCopied(""), 2000); }}
                        className="shrink-0 text-white/50 hover:text-[#FF3B00] transition-colors"
                        title="Copy link"
                      >
                        {copied === "new" ? <Check className="w-4 h-4 text-[#00FF41]" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                )
              )}
            </div>

            <div className="border border-white/10 overflow-x-auto" data-testid="admin-table-wrap">
              <table className="w-full text-left font-mono2 text-xs" data-testid="admin-table">
                <thead>
                  <tr className="border-b border-white/10 text-white/40 uppercase tracking-[0.2em]">
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Registered</th>
                    <th className="px-4 py-3">Verified at</th>
                    <th className="px-4 py-3">Link</th>
                    <th className="px-4 py-3">Remove</th>
                  </tr>
                </thead>
                <tbody>
                  {data.subscribers.map((s) => (
                    <tr key={s.email} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors" data-testid={`admin-row-${s.email}`}>
                      <td className="px-4 py-3 text-white/80">{s.email}</td>
                      <td className="px-4 py-3">
                        {s.verified ? (
                          <span className="text-[#00FF41]">VERIFIED</span>
                        ) : (
                          <span className="text-[#F5A623]">PENDING</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-white/40">{s.created_at?.slice(0, 19).replace("T", " ")}</td>
                      <td className="px-4 py-3 text-white/40">{s.verified_at ? s.verified_at.slice(0, 19).replace("T", " ") : "—"}</td>
                      <td className="px-4 py-3">
                        <button
                          data-testid={`copy-link-${s.email}`}
                          onClick={() => copyLink(s.token, s.email)}
                          className="text-white/40 hover:text-[#FF3B00] transition-colors"
                          title="Copy download link"
                        >
                          {copied === s.email ? <Check className="w-3.5 h-3.5 text-[#00FF41]" /> : <Copy className="w-3.5 h-3.5" />}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          data-testid={`remove-${s.email}`}
                          onClick={() => removeEmail(s.email)}
                          className="text-white/40 hover:text-[#FF3B00] transition-colors"
                          title="Remove from list"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {data.subscribers.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-white/35 text-center" data-testid="admin-empty">
                        No registrations yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <p className="mt-4 font-mono2 text-[11px] text-white/35 uppercase tracking-[0.2em]">
              CSV export includes verified emails only — unverified entries stay out of the list.
            </p>
            <button data-testid="admin-clear-button" onClick={clearAll} className="btn-ghost mt-6">
              <Trash2 className="w-4 h-4" /> Clear entire list
            </button>

            <div className="mt-14" data-testid="support-messages">
              <div className="flex items-center gap-3 mb-5">
                <LifeBuoy className="w-5 h-5 text-[#FF3B00]" />
                <h2 className="font-display uppercase tracking-tight text-2xl">Support messages</h2>
              </div>
              {messages.length === 0 ? (
                <p className="font-mono2 text-xs text-white/35" data-testid="support-empty">No messages yet.</p>
              ) : (
                <div className="space-y-3">
                  {messages.map((m) => (
                    <div key={m.id} className="grid-cell p-5" data-testid={`support-message-${m.id}`}>
                      <div className="flex items-center justify-between gap-4 mb-2">
                        <p className="font-mono2 text-xs text-white/70">
                          {m.name ? `${m.name} · ` : ""}<span className="text-white">{m.email}</span>
                        </p>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className="font-mono2 text-[11px] text-white/35">{m.created_at?.slice(0, 16).replace("T", " ")}</span>
                          <button
                            data-testid={`support-delete-${m.id}`}
                            onClick={() => deleteMessage(m.id)}
                            className="text-white/40 hover:text-[#FF3B00] transition-colors"
                            title="Delete message"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                      <p className="text-sm text-white/70 whitespace-pre-wrap leading-relaxed">{m.message}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="mt-14" data-testid="forum-admin">
              <div className="flex items-center gap-3 mb-5">
                <MessageSquare className="w-5 h-5 text-[#FF3B00]" />
                <h2 className="font-display uppercase tracking-tight text-2xl">Forum threads</h2>
              </div>
              {threads.length === 0 ? (
                <p className="font-mono2 text-xs text-white/35" data-testid="forum-empty">No threads yet.</p>
              ) : (
                <div className="space-y-3">
                  {threads.map((t) => (
                    <div key={t.id} className="grid-cell" data-testid={`forum-thread-${t.id}`}>
                      <button onClick={() => toggleThread(t.id)} className="w-full p-5 flex items-center justify-between gap-4 text-left hover:bg-white/[0.03] transition-colors" data-testid={`forum-expand-${t.id}`}>
                        <div>
                          <p className="text-sm text-white font-medium">{t.title}</p>
                          <p className="font-mono2 text-[11px] text-white/40 mt-1">
                            {t.author_name} · {t.author_email} · {t.replies} {t.replies === 1 ? "reply" : "replies"} · {t.last_activity?.slice(0, 16).replace("T", " ")}
                          </p>
                        </div>
                        <span className="flex items-center gap-4 shrink-0">
                          <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-white/30">{expanded === t.id ? "Close" : "View"}</span>
                          <span
                            data-testid={`forum-delete-${t.id}`}
                            role="button"
                            onClick={(e) => { e.stopPropagation(); deleteThread(t.id); }}
                            className="text-white/40 hover:text-[#FF3B00] transition-colors"
                            title="Delete thread and all replies"
                          >
                            <Trash2 className="w-4 h-4" />
                          </span>
                        </span>
                      </button>
                      {expanded === t.id && (
                        <div className="border-t border-white/10 p-5 space-y-3" data-testid={`forum-expanded-${t.id}`}>
                          {expandedReplies.length === 0 ? (
                            <p className="font-mono2 text-xs text-white/35">No replies in this thread.</p>
                          ) : (
                            expandedReplies.map((r) => (
                              <div key={r.id} className="flex items-start justify-between gap-4 border-b border-white/5 last:border-0 pb-3" data-testid={`admin-reply-${r.id}`}>
                                <div>
                                  <p className="font-mono2 text-[11px] text-white/40 mb-1">
                                    <span className="text-[#FF3B00]">{r.author_name}</span> · {r.author_email} · {r.created_at?.slice(0, 16).replace("T", " ")}
                                  </p>
                                  <p className="text-sm text-white/70 whitespace-pre-wrap">{r.body}</p>
                                </div>
                                <button
                                  data-testid={`reply-delete-${r.id}`}
                                  onClick={() => deleteReply(r.id)}
                                  className="text-white/40 hover:text-[#FF3B00] transition-colors shrink-0"
                                  title="Delete this reply"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
